<template>
    <section v-if="slice.items?.length" class="slice slice-verbatim">
        <div class="slice-content slice-verbatim__content">
            <CardHand>
                <MaltImg v-if="currentItem?.photo.url" :src="currentItem.photo.url" :alt="currentItem.photo.alt ?? ''" width="219" />
            </CardHand>
            <div>
                <div class="slice-verbatim__quote">
                    <div class="slice-verbatim__quote-text">
                        <PrismicRichText :field="currentItem?.quote" />
                    </div>
                    <div class="slice-verbatim__name">{{ currentItem?.name }}</div>
                    <div class="slice-verbatim__job">{{ currentItem?.job }}</div>
                </div>
                <div class="slice-verbatim__controls">
                    <VJoyIndicators v-if="slice.items?.length > 1" v-model="currentVerbatim" variant="default">
                        <VJoyIndicator v-for="(_, index) in slice.items" :key="`verbatim-indicator-${index}`" :value="String(index)" />
                    </VJoyIndicators>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {InvitationVerbatimSlice} from '@prismicio-types';
    import {MaltImg} from '#components';
    import {VJoyIndicator, VJoyIndicators} from '@maltjoy/core-vue';
    import CardHand from '../../components/CardHand.vue';

    // Props
    const {slice} = defineProps(getSliceComponentProps<InvitationVerbatimSlice>(['slice', 'index', 'slices', 'context']));

    // Refs
    const currentVerbatim = ref<string>('0');

    // Computed
    const currentVerbatimNumber = computed(() => Number(currentVerbatim.value));
    const currentItem = computed(() => slice.items[currentVerbatimNumber.value]);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .slice-verbatim {
        background-color: var(--joy-color-neutral-10);
        padding-bottom: 80px;

        &__content {
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: left;

            @include mq.screen_md(min) {
                flex-direction: row;
                gap: 80px;
            }
        }

        &__quote {
            position: relative;
            padding: 80px var(--joy-core-spacing-6);

            &::before,
            &::after {
                content: '“';
                position: absolute;
                @include text.getFontBySize(secondary-800);
                font-size: 142px;
            }

            &::before {
                top: 0;
                left: 0;

                @include mq.screen_md(min) {
                    left: -40px;
                }
            }

            &::after {
                bottom: 100px;
                right: 0;
                /* Agrandir doesn't have a right double quote char */
                transform: rotate(180deg);
            }
        }

        &__quote-text {
            @include text.getFontBySize(primary-800);
            line-height: 125%;
            margin: 0;
            padding-bottom: var(--joy-core-spacing-8);
        }

        &__name {
            @include text.getFontBySize(primary-800);
            font-weight: 700;
            letter-spacing: var(--joy-font-letter-spacing-tight);
        }

        &__job {
            @include text.getFontBySize(primary-800);
        }

        &__controls {
            display: flex;
            justify-content: center;
            @include mq.screen_md(min) {
                padding-left: var(--joy-core-spacing-6);
                justify-content: flex-start;
            }
        }
    }
</style>
